import { dateRangeOperations } from '@ardoq/date-range';
import { formatDateShort, formatDateTimeShort } from '@ardoq/date-time';
import type { I18nLocale } from '@ardoq/i18n';
import { formatNumber, Locale } from '@ardoq/locale';
import { markdownToSanitizedHTML } from '@ardoq/markdown';
import * as encodingUtils from '@ardoq/html';
import { isNil } from 'lodash';

const stripHtml = (html: string) =>
  new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';

type FormatNumberFieldValueArgs = {
  value: unknown;
  numberFormatOption?: string;
  locale?: Locale;
  isAbbreviated?: boolean;
};
const formatNumberFieldValue = ({
  value,
  numberFormatOption = 'default',
  locale,
  isAbbreviated,
}: FormatNumberFieldValueArgs): string | null => {
  const number = !isNil(value) ? Number(value) : null;
  if (number === null || isNaN(number)) {
    return null;
  }
  return formatNumber(number, numberFormatOption, locale, isAbbreviated);
};

const stringValue = (value: unknown, isStrict?: boolean): string | null =>
  isStrict ? (typeof value === 'string' ? value : null) : String(value);

const formatTextAreaFieldValue = (
  value: unknown,
  escapeHTML?: boolean
): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  // val will be a markdown formatted string.
  // the legacy behavior is to return it as HTML.
  // if escapeHTML is false, we will strip the HTML.
  const result = markdownToSanitizedHTML(value, 'permissive');
  return escapeHTML ? result : stripHtml(result);
};

type FormatDateTimeFieldValueArgs = {
  value: unknown;
  locale: Locale;
  i18nLocale: I18nLocale;
  fieldName: string;
  excludeTime?: boolean;
};
const formatDateTimeFieldValue = ({
  value,
  locale,
  i18nLocale,
  fieldName,
  excludeTime,
}: FormatDateTimeFieldValueArgs): string | null => {
  if (value === null) {
    return dateRangeOperations.getDefinedEmptyDateRangeFieldValueString(
      fieldName,
      i18nLocale
    );
  }
  if (typeof value !== 'string') {
    return null;
  }

  const escapedVal = encodingUtils.escapeHTML(value);

  return (
    (excludeTime
      ? formatDateShort(escapedVal, locale)
      : formatDateTimeShort(escapedVal, locale)) ?? escapedVal
  );
};

const formatSelectMultipleListFieldValue = (value: unknown): string | null => {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  return value.join(', ');
};

const formatDateRangeFieldValue = (
  value: unknown,
  format = formatDateTimeShort,
  locale: Locale
): string | null => {
  if (!value) {
    return null;
  }

  /**
   * The field model formatting method handles the value as an array of two,
   * the rest of the app handles it as an object with start and end properties.
   *
   * Because the interface is meant to substitute the field model method, we
   * handle both cases here, as the reasons for this discrepancy are unknown.
   */

  // model method
  if (Array.isArray(value) && value.length === 2) {
    return `${format(value[0], locale)} - ${format(value[1], locale)}`;
  }

  // interface method
  const { start, end } = value as {
    start: string | null;
    end: string | null;
  };
  return start || end
    ? `${start ? format(start, locale) : ''} - ${
        end ? format(end, locale) : ''
      }`
    : null;
};

const formatDateTimeRangeFieldValue = (value: unknown, locale: Locale) =>
  formatDateRangeFieldValue(value, formatDateTimeShort, locale);

const formatDateOnlyRangeFieldValue = (value: unknown, locale: Locale) =>
  formatDateRangeFieldValue(value, formatDateShort, locale);

export const fieldValueFormatters = {
  formatNumberFieldValue,
  stringValue,
  formatTextAreaFieldValue,
  formatDateTimeFieldValue,
  formatSelectMultipleListFieldValue,
  formatDateTimeRangeFieldValue,
  formatDateOnlyRangeFieldValue,
  formatDateRangeFieldValue,
};
